<script setup lang="ts">
import { nextTick, onMounted, ref, computed } from "vue";
import { HeaderNavigationInterface } from "@interfaces/HeaderNavigationInterface.ts";
import { usePage, Link, router } from "@inertiajs/vue3";
import { PagePropsInterface } from "@interfaces/PagePropsInterface.ts";
import LanguageSwitch from "@components/LanguageSwitch.vue";
import { trans } from 'laravel-vue-i18n';

let page = usePage<PagePropsInterface>();
const submenus = ref([]);
const open = ref<boolean>(false);

const navigation = computed<Array<HeaderNavigationInterface>>(() => [
    {
        name: 'Standing desks',
        url: route('preconfig.all'),
    },
    {
        name: trans('sections.for_who'),
        children: page.props.categories,
    },
    {
        name: trans('sections.quality'),
        url: route('quality')
    },
    {
        name: 'Blog',
        url: route('blogs')
    },
]);

onMounted(() => {
    window.addEventListener('resize', () => {
        window.innerWidth > 1024 ? open.value = false : null;
    });
});

router.on('before', () => {
    submenus.value.forEach((submenu: HTMLElement) => {
        submenu.style.display = 'none';
        setTimeout(() => {
            nextTick(() => {
                submenu.style.removeProperty('display');
            });
        }, 100);
    });
});

const closeMenu = () => {
    open.value = false;
}

const openMenu = () => {
    open.value = true;
}
</script>

<template>
    <section :class="{
        'transparent': page.props.headerOptions?.transparent,
        'inverted': page.props.headerOptions?.inverted,
        'open': open
    }" id="layouts-section-header">
        <div class="container">
            <div class="logo-button">
                <div class="logo">
                    <Link :href="route('home')" class="logo-link">
                    <img v-if="!page.props.headerOptions?.inverted || open" :src="page.props.application.logo.normal"
                        alt="logo LevelUp Desk" />
                    <img v-else :src="page.props.application.logo.white" alt="logo LevelUp Desk" />
                    </Link>
                </div>
                <div class="open-button">
                    <button aria-label="Open menu" v-if="!open" @click="openMenu">
                        <i class="fas fa-bars"></i>
                    </button>
                    <button aria-label="Close menu" v-else @click="closeMenu">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </div>
            <header :class="{ 'open': open }" class="header">
                <div class="header-left">
                    <nav class="navigation">
                        <ul>
                            <li v-for="nav in navigation" :key="nav.name">
                                <Link @click="closeMenu" class="nav-link" v-if="nav.url" :href="nav.url">
                                <span>{{ nav.name }}</span>
                                <i v-if="nav.children" class="fas fa-chevron-down"></i>
                                </Link>
                                <span aria-haspopup="true" tabindex="-1" class="nav-link" v-else>
                                    {{ nav.name }}
                                    <i v-if="nav.children" class="fas fa-chevron-down"></i>
                                </span>

                                <ul ref="submenus" v-if="nav.children" class="sub-menu">
                                    <li v-for="child in nav.children" :key="child.slug">
                                        <Link @click="closeMenu" :href="child.href">
                                        <p class="name">
                                            {{ child.name }}
                                        </p>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </nav>
                </div>
                <div class="header-right">
                    <LanguageSwitch />
                    <div class="cart">
                        <Link aria-label="Winkelmand" :href="route('shoppingcart.preview')">
                        <i class="far fa-shopping-cart"></i>
                        <div v-if="page.props.cartCount > 0" class="cart-count">{{ page.props.cartCount }}</div>
                        </Link>
                    </div>
                </div>
            </header>
        </div>
    </section>
</template>

<style scoped lang="scss">
@use "@scss/layouts/sections/header";
</style>
